import { useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material';

import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import Autocomplete from '~/components/ui/Autocomplete';
import { Button } from '~/components/ui/button';
import Select from '~/components/ui/Select';
import Tabs from '~/components/ui/Tabs';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH, GOOGLE_MAP_KEY, TABLET_MIN_WIDTH } from '~/constants/common';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import { COLORS } from '~/styles/colors';
import { VCardPlusData } from '~/types/qrCode';
import { getLocationTypeByTab, isMaxLength } from '~/utils/common';
import { parsePlaceDetails } from '~/utils/google-maps/adress';

const getLocationItem = (value, nextValue) => {
  if (value && nextValue) {
    return `${value}, `;
  }

  return value || '';
};

const getDefaultAddress = (location) => {
  if (!location) return '';

  return `${getLocationItem(location?.street, location?.number)}${getLocationItem(location?.number, location?.postalCode)}${getLocationItem(location?.postalCode, location?.city)}${getLocationItem(location?.city, location?.country)}${getLocationItem(location?.country)}`;
};

type VCardPlusLocationProps = {
  updateVCardPlusLocation?: () => void;
  vCardPlusData: VCardPlusData;
};
export default function VCardPlusLocation({ updateVCardPlusLocation, vCardPlusData }: VCardPlusLocationProps) {
  const { t } = useTranslation();

  const isDesktop = useSelector(getIsDesktop);

  const locationTabs = [
    {
      value: 'HIDE_ADDRESS_LINK',
      label: t('other:hideLink'),
    },
    {
      value: 'SHOW_ADDRESS_LINK',
      label: t('other:showLink'),
    },
    {
      value: 'SHOW_MAP',
      label: t('other:showMap'),
    },
  ];

  const tabsList = [
    { value: '1', label: t('template5:complete') },
    { value: '2', label: t('template5:url') },
    { value: '3', label: t('template5:coordinate') },
  ];

  const [currentTab, setCurrentTab] = useState('1');
  const [isManually, setManually] = useState(
    vCardPlusData?.location?.city ||
      vCardPlusData?.location?.country ||
      vCardPlusData?.location?.street ||
      vCardPlusData?.location?.state,
  );
  const [address, setAddress] = useState(getDefaultAddress(vCardPlusData?.location));
  const [street, setStreet] = useState(vCardPlusData?.location?.street);
  const [number, setNumber] = useState(vCardPlusData?.location?.number || '');
  const [postalCode, setPostalCode] = useState(vCardPlusData?.location?.postalCode);
  const [city, setCity] = useState(vCardPlusData?.location?.city);
  const [state, setState] = useState(vCardPlusData?.location?.state);
  const [country, setCountry] = useState(vCardPlusData?.location?.country);
  const [longitude, setLongitude] = useState(vCardPlusData?.location?.longitude);
  const [latitude, setLatitude] = useState(vCardPlusData?.location?.latitude);
  const [locationVisibility, setLocationVisibility] = useState(vCardPlusData?.location?.locationVisibility);
  const [url, setUrl] = useState(vCardPlusData?.location?.url || '');

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: GOOGLE_MAP_KEY,
  });

  const handleUpdateField = ({ field, value }) => {
    if (vCardPlusData?.location?.id) {
      updateVCardPlusLocation({
        body: {
          [field]: value,
        },
        locationId: vCardPlusData.location?.id,
      });
    }
  };

  const handleTabItemClick = (event, newValue) => {
    setCurrentTab(newValue);
    handleUpdateField({ field: 'type', value: getLocationTypeByTab(newValue) });
  };

  const handlePreselectPlaceDetails = (placeDetails) => {
    setManually(true);

    const { city, country, postalCode, state, street, number } = parsePlaceDetails(placeDetails);

    setCity(city);
    setPostalCode(postalCode);
    setCountry(country);
    setNumber(number);
    setStreet(street);
    setState(state);

    if (vCardPlusData?.location?.id) {
      updateVCardPlusLocation({
        body: {
          street_number_first: false,
          street: street || '',
          number: number || null,
          postal_code: postalCode || '',
          city: city || '',
          state: state || '',
          country: country || '',
        },
        locationId: vCardPlusData.location?.id,
      });
    }
  };

  const handlePlaceChange = (_, value) => {
    const selectedPlace = placePredictions.find((item) => item.description === value);
    setAddress(selectedPlace.description);

    placesService?.getDetails(
      {
        placeId: selectedPlace.place_id,
      },
      (placeDetails) => handlePreselectPlaceDetails(placeDetails),
    );
  };

  const handleManually = () => {
    setManually((prev) => !prev);
    if (isManually) {
      setStreet('');
      setNumber(null);
      setPostalCode('');
      setCity('');
      setState('');
      setCountry('');

      if (vCardPlusData?.location?.id) {
        updateVCardPlusLocation({
          body: {
            street_number_first: false,
            street: '',
            number: null,
            postal_code: '',
            city: '',
            state: '',
            country: '',
          },
          locationId: vCardPlusData.location?.id,
        });
      }
    }
  };

  const handleChangeNumber = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value)) {
      setNumber(value);
    }
  };

  return (
    <Accordion
      variant="child"
      defaultExpanded
      summary={t('other:location')}
      details={
        <>
          {isDesktop ? (
            <StyledTabs>
              {tabsList.map(({ label, value }) => {
                const isSelected = value === currentTab;

                return (
                  <StyledTab
                    key={value}
                    $isSelected={isSelected}
                    onClick={() => {
                      handleUpdateField({ field: 'type', value: getLocationTypeByTab(value) });
                      setCurrentTab(value);
                    }}
                  >
                    {label}
                  </StyledTab>
                );
              })}
            </StyledTabs>
          ) : (
            <Tabs tabs={tabsList} value={currentTab} onChange={handleTabItemClick} />
          )}

          {currentTab === '1' && (
            <>
              <Select
                label={t('template5:locationOpt')}
                items={locationTabs}
                value={locationVisibility}
                onChange={(e) => {
                  setLocationVisibility(e.target.value);
                  handleUpdateField({ field: 'location_visibility', value: e.target.value });
                }}
                fullWidth
              />
              <StyledAutocompleteContainer>
                <Autocomplete
                  freeSolo
                  fullWidth
                  disabled={isManually}
                  disableClearable
                  options={placePredictions.map((option) => option.description)}
                  onChange={handlePlaceChange}
                  value={address}
                  placeholder={`${t('common:startTypingHere')}...`}
                  label={t('template5:searchAddress')}
                  onTextInputChange={(evt) => {
                    getPlacePredictions({
                      input: evt.target.value,
                    });
                    setAddress(evt.target.value);
                  }}
                />

                <Button
                  sx={{ whiteSpace: 'nowrap' }}
                  type="primary-outlined"
                  onClick={handleManually}
                  size="xs"
                  color={isManually ? COLORS.error : undefined}
                  borderColor={isManually ? COLORS.error : undefined}
                >
                  {!isManually ? t('template5:manual') : t('template5:delete_address')}
                </Button>
              </StyledAutocompleteContainer>
              {isManually && (
                <>
                  <StyledManuallyBlock>
                    <StyledManuallyHeaderLeft>
                      <TextInput
                        fullWidth
                        label={t('template5:street')}
                        helperText={isMaxLength(street, 70)}
                        onChange={(e) => setStreet(e.target.value)}
                        onBlur={() => handleUpdateField({ field: 'street', value: street })}
                        inputProps={{ maxLength: 70 }}
                        value={street}
                        defaultValue={street}
                        placeholder={t('common:typeHere')}
                      />
                    </StyledManuallyHeaderLeft>
                    <StyledManuallyHeaderRight>
                      <TextInput
                        fullWidth
                        label={t('template5:strNumber')}
                        onChange={handleChangeNumber}
                        onBlur={() => handleUpdateField({ field: 'number', value: +number })}
                        value={number}
                        helperText={isMaxLength(number, 40)}
                        inputProps={{ maxLength: 40, inputMode: 'numeric', pattern: '[0-9]*' }}
                        defaultValue={number}
                        placeholder={t('common:typeHere')}
                      />
                      <TextInput
                        fullWidth
                        label={t('template5:postCode')}
                        onChange={(e) => setPostalCode(e.target.value)}
                        onBlur={() => handleUpdateField({ field: 'postal_code', value: postalCode })}
                        helperText={isMaxLength(postalCode, 40)}
                        inputProps={{ maxLength: 40 }}
                        value={postalCode}
                        defaultValue={postalCode}
                        placeholder={t('common:typeHere')}
                      />
                    </StyledManuallyHeaderRight>
                  </StyledManuallyBlock>
                  <StyledManuallyBlock>
                    <TextInput
                      fullWidth
                      label={t('template5:city')}
                      onChange={(e) => setCity(e.target.value)}
                      onBlur={() => handleUpdateField({ field: 'city', value: city })}
                      helperText={isMaxLength(city, 70)}
                      inputProps={{ maxLength: 70 }}
                      value={city}
                      defaultValue={city}
                      placeholder={t('common:typeHere')}
                    />
                    <TextInput
                      fullWidth
                      label={t('template5:state')}
                      onChange={(e) => setState(e.target.value)}
                      onBlur={() => handleUpdateField({ field: 'state', value: state })}
                      helperText={isMaxLength(state, 70)}
                      inputProps={{ maxLength: 70 }}
                      value={state}
                      defaultValue={state}
                      placeholder={t('common:typeHere')}
                    />
                  </StyledManuallyBlock>
                  <TextInput
                    fullWidth
                    label={t('template5:country')}
                    onChange={(e) => setCountry(e.target.value)}
                    onBlur={() => handleUpdateField({ field: 'country', value: country })}
                    helperText={isMaxLength(country, 70)}
                    inputProps={{ maxLength: 70 }}
                    value={country}
                    defaultValue={country}
                    placeholder={t('common:typeHere')}
                  />
                </>
              )}
            </>
          )}
          {currentTab === '2' && (
            <UrlTextField
              requireProtocol={false}
              urlValue={url}
              placeholder={t('template5:url')}
              onChange={(e) => {
                setUrl(e.target.value);
              }}
              onBlur={({ isValid }) => isValid && handleUpdateField({ field: 'url', value: url })}
              label={t('labels:url')}
            />
          )}
          {currentTab === '3' && (
            <StyledCoordinatesContainer>
              <TextInput
                fullWidth
                defaultValue={latitude}
                label={t('template5:latitude')}
                helperText={isMaxLength(latitude, 50)}
                inputProps={{ maxLength: 50 }}
                onBlur={() => handleUpdateField({ field: 'latitude', value: latitude })}
                placeholder={`${t('common:eg')} 00.000000`}
                onChange={(e) => setLatitude(e.target.value)}
              />
              <TextInput
                fullWidth
                defaultValue={longitude}
                helperText={isMaxLength(longitude, 50)}
                inputProps={{ maxLength: 50 }}
                label={t('template5:longitude')}
                onBlur={() => handleUpdateField({ field: 'longitude', value: longitude })}
                placeholder={`${t('common:eg')} 00.000000`}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </StyledCoordinatesContainer>
          )}
        </>
      }
    />
  );
}

export const StyledTabs = styled('div')(() => {
  return {
    width: '100%',
    borderRadius: '8px',
    display: 'flex',
    padding: '8px 32px',
    gap: '20px',
    border: `1px solid ${COLORS.grey200}`,
    marginBottom: '16px',
    backgroundColor: COLORS.white,
  };
});

export const StyledTab = styled('button')(({ $isSelected }) => {
  return {
    borderRadius: '8px',
    flex: 1,
    cursor: 'pointer',
    border: `1px solid ${$isSelected ? COLORS.textMain : COLORS.grey250}`,
    color: $isSelected ? COLORS.textMain : COLORS.grey250,
    fontSize: '14px',
    fontWeight: 500,
    padding: '4px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '19px',
    background: 'none',
  };
});

export const StyledAutocompleteContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '8px',
    alignItems: 'end',
    marginBottom: '24px',
    paddingTop: '24px',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'center',
    },
  };
});

export const StyledCoordinatesContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flexDirection: 'column',
    },
  };
});

export const StyledManuallyBlock = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flexDirection: 'column',
    },
  };
});

export const StyledManuallyHeaderLeft = styled('div')(({ theme }) => {
  return {
    flex: 1,
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flex: 'none',
    },
  };
});

export const StyledManuallyHeaderRight = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    flex: 1,
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flex: 2,
    },
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flexDirection: 'column',
      flex: 'none',
    },
  };
});
